// ── Input Manager ──
// Singleton that captures keyboard, mouse button, and mouse movement state.
// Provides bitfield encoding for network transmission.
// Movement/action key codes are resolved from the KeybindManager at read time
// so that rebinds take effect immediately without re-registering listeners.

import { keybindManager } from './keybinds';

/** Snapshot of input state for a single tick (used by player controller). */
export interface InputState {
  forward: boolean;
  back: boolean;
  left: boolean;
  right: boolean;
  jump: boolean;
  crouch: boolean;
  walk: boolean;
  fire: boolean;
  reload: boolean;
  ads: boolean;
  mouseDx: number;
  mouseDy: number;
}

// ── Bitfield constants ──
const KEY_W     = 1 << 0; // 1
const KEY_A     = 1 << 1; // 2
const KEY_S     = 1 << 2; // 4
const KEY_D     = 1 << 3; // 8
const KEY_JUMP  = 1 << 4; // 16
const KEY_CROUCH = 1 << 5; // 32
const KEY_WALK  = 1 << 6; // 64

const ACT_FIRE    = 1 << 0; // 1
const ACT_RELOAD  = 1 << 1; // 2
const ACT_USE     = 1 << 2; // 4  (E-key interact: doors / pickups / defuse / plant)
const ACT_DROP    = 1 << 3; // 8  (drop currently equipped weapon)
const ACT_INSPECT = 1 << 4; // 16

class InputManager {
  // ── Keyboard state ──
  private keys = new Map<string, boolean>();

  // ── Mouse button state (0 = left, 1 = middle, 2 = right) ──
  private mouseButtons = new Set<number>();

  // ── Accumulated mouse movement since last reset ──
  private mouseDx = 0;
  private mouseDy = 0;

  // ── Captured snapshot delta (for consistent encode reads) ──
  private snapshotDx = 0;
  private snapshotDy = 0;

  // ── Bound handler references for cleanup ──
  private onKeyDown: (e: KeyboardEvent) => void;
  private onKeyUp: (e: KeyboardEvent) => void;
  private onMouseDown: (e: MouseEvent) => void;
  private onMouseUp: (e: MouseEvent) => void;
  private onMouseMove: (e: MouseEvent) => void;
  private onContextMenu: (e: Event) => void;

  private _initialized = false;

  constructor() {
    this.onKeyDown = (e: KeyboardEvent) => {
      this.keys.set(e.code, true);
    };
    this.onKeyUp = (e: KeyboardEvent) => {
      this.keys.set(e.code, false);
    };
    this.onMouseDown = (e: MouseEvent) => {
      this.mouseButtons.add(e.button);
    };
    this.onMouseUp = (e: MouseEvent) => {
      this.mouseButtons.delete(e.button);
    };
    this.onMouseMove = (e: MouseEvent) => {
      // movementX/Y are only meaningful under pointer lock
      this.mouseDx += e.movementX;
      this.mouseDy += e.movementY;
    };
    this.onContextMenu = (e: Event) => {
      e.preventDefault();
    };
  }

  /** Attach event listeners to the document. Call once at startup. */
  init(): void {
    if (this._initialized) {
      console.warn('InputManager.init() called twice — ignoring');
      return;
    }
    this._initialized = true;

    // Remove any stale listeners before adding (defensive)
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('keyup', this.onKeyUp);
    document.removeEventListener('mousedown', this.onMouseDown);
    document.removeEventListener('mouseup', this.onMouseUp);
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('contextmenu', this.onContextMenu);

    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('keyup', this.onKeyUp);
    document.addEventListener('mousedown', this.onMouseDown);
    document.addEventListener('mouseup', this.onMouseUp);
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('contextmenu', this.onContextMenu);
  }

  /** Remove all event listeners. Call on shutdown. */
  destroy(): void {
    if (!this._initialized) return;
    this._initialized = false;

    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('keyup', this.onKeyUp);
    document.removeEventListener('mousedown', this.onMouseDown);
    document.removeEventListener('mouseup', this.onMouseUp);
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('contextmenu', this.onContextMenu);

    this.keys.clear();
    this.mouseButtons.clear();
    this.mouseDx = 0;
    this.mouseDy = 0;
  }

  // ── Queries ──

  isKeyDown(code: string): boolean {
    return this.keys.get(code) === true;
  }

  isMouseDown(button: number): boolean {
    return this.mouseButtons.has(button);
  }

  getMouseDelta(): { dx: number; dy: number } {
    return { dx: this.mouseDx, dy: this.mouseDy };
  }

  resetMouseDelta(): void {
    this.mouseDx = 0;
    this.mouseDy = 0;
  }

  // ── Snapshot (for game tick consumption) ──

  /** Build an InputState snapshot and reset mouse delta. */
  snapshot(): InputState {
    // Capture mouse delta at snapshot time for consistent encoding
    this.snapshotDx = this.mouseDx;
    this.snapshotDy = this.mouseDy;
    this.resetMouseDelta();

    const kb = keybindManager;
    const forwardKey = kb.getKeyForAction('forward') ?? 'KeyW';
    const backKey = kb.getKeyForAction('backward') ?? 'KeyS';
    const leftKey = kb.getKeyForAction('left') ?? 'KeyA';
    const rightKey = kb.getKeyForAction('right') ?? 'KeyD';
    const jumpKey = kb.getKeyForAction('jump') ?? 'Space';
    const crouchKey = kb.getKeyForAction('crouch') ?? 'ControlLeft';
    const walkKey = kb.getKeyForAction('walk') ?? 'ShiftLeft';
    const reloadKey = kb.getKeyForAction('reload') ?? 'KeyR';
    const fireKey = kb.getKeyForAction('fire') ?? 'Mouse0';
    const aimKey = kb.getKeyForAction('aim') ?? 'Mouse2';

    const state: InputState = {
      forward: this.isKeyDown(forwardKey),
      back:    this.isKeyDown(backKey),
      left:    this.isKeyDown(leftKey),
      right:   this.isKeyDown(rightKey),
      jump:    this.isKeyDown(jumpKey),
      crouch:  this.isKeyDown(crouchKey),
      walk:    this.isKeyDown(walkKey),
      fire:    fireKey.startsWith('Mouse') ? this.isMouseDown(parseInt(fireKey.charAt(5))) : this.isKeyDown(fireKey),
      reload:  this.isKeyDown(reloadKey),
      ads:     aimKey.startsWith('Mouse') ? this.isMouseDown(parseInt(aimKey.charAt(5))) : this.isKeyDown(aimKey),
      mouseDx: this.snapshotDx,
      mouseDy: this.snapshotDy,
    };
    return state;
  }

  /** Get the mouse delta captured at the last snapshot. */
  getSnapshotDelta(): { dx: number; dy: number } {
    return { dx: this.snapshotDx, dy: this.snapshotDy };
  }

  // ── Network encoding ──

  /**
   * Pack movement keys into a single ubyte.
   * Bits: W=1, A=2, S=4, D=8, Jump=16, Crouch=32, Walk=64
   */
  encodeKeys(): number {
    const kb = keybindManager;
    let bits = 0;
    if (this.isKeyDown(kb.getKeyForAction('forward') ?? 'KeyW'))     bits |= KEY_W;
    if (this.isKeyDown(kb.getKeyForAction('left') ?? 'KeyA'))        bits |= KEY_A;
    if (this.isKeyDown(kb.getKeyForAction('backward') ?? 'KeyS'))    bits |= KEY_S;
    if (this.isKeyDown(kb.getKeyForAction('right') ?? 'KeyD'))       bits |= KEY_D;
    if (this.isKeyDown(kb.getKeyForAction('jump') ?? 'Space'))       bits |= KEY_JUMP;
    if (this.isKeyDown(kb.getKeyForAction('crouch') ?? 'ControlLeft')) bits |= KEY_CROUCH;
    if (this.isKeyDown(kb.getKeyForAction('walk') ?? 'ShiftLeft'))   bits |= KEY_WALK;
    return bits;
  }

  /**
   * Pack action flags into a single ubyte.
   * Bits: Fire=1, Reload=2, Use=4, Drop=8, Inspect=16
   * (Use/Drop are populated by other call sites that build ClientInput directly.)
   */
  encodeActions(): number {
    const kb = keybindManager;
    const fireKey = kb.getKeyForAction('fire') ?? 'Mouse0';
    const reloadKey = kb.getKeyForAction('reload') ?? 'KeyR';
    const inspectKey = kb.getKeyForAction('inspect') ?? 'KeyF';
    const useKey = kb.getKeyForAction('interact') ?? 'KeyE';
    const dropKey = kb.getKeyForAction('drop');
    let bits = 0;
    if (fireKey.startsWith('Mouse') ? this.isMouseDown(parseInt(fireKey.charAt(5))) : this.isKeyDown(fireKey)) bits |= ACT_FIRE;
    if (this.isKeyDown(reloadKey))     bits |= ACT_RELOAD;
    if (this.isKeyDown(useKey))        bits |= ACT_USE;
    if (dropKey && this.isKeyDown(dropKey)) bits |= ACT_DROP;
    if (this.isKeyDown(inspectKey))    bits |= ACT_INSPECT;
    return bits;
  }
}

// ── Singleton export ──
export const input = new InputManager();

/**
 * Pure helper: pack movement+walk bits from an already-captured InputState.
 * Matches encodeKeys bit layout so touch providers can feed the same wire format.
 */
export function encodeKeysFromState(state: InputState): number {
  let bits = 0;
  if (state.forward) bits |= KEY_W;
  if (state.left)    bits |= KEY_A;
  if (state.back)    bits |= KEY_S;
  if (state.right)   bits |= KEY_D;
  if (state.jump)    bits |= KEY_JUMP;
  if (state.crouch)  bits |= KEY_CROUCH;
  if (state.walk)    bits |= KEY_WALK;
  return bits;
}

/**
 * Pure helper: pack action bits from an already-captured InputState.
 * `inspect`, `use`, and `drop` are supplied separately because InputState
 * doesn't carry them (touch providers expose them as discrete buttons).
 */
export function encodeActionsFromState(
  state: InputState,
  inspect = false,
  use = false,
  drop = false,
): number {
  let bits = 0;
  if (state.fire)   bits |= ACT_FIRE;
  if (state.reload) bits |= ACT_RELOAD;
  if (use)          bits |= ACT_USE;
  if (drop)         bits |= ACT_DROP;
  if (inspect)      bits |= ACT_INSPECT;
  return bits;
}
